import { z } from "zod";

import { formatCurrency } from "@ca/formatters/currency";
import { roundCurrency2 } from "@ca/policies/premium-math";

export const DEFAULT_HULL_WAR_RATE = 0.02;

export const AIRCRAFT_PREMIUM_COST_LABEL = "Total Aircraft Premium";
export const CGL_PREMIUM_COST_LABEL = "Total CGL Premium";
export const UAS_PREMIUM_COST_LABEL = "Total UAS Premium";
export const UAS_CHEMICAL_PREMIUM_COST_LABEL = "Chemical Liability Premium";
export const ENDORSEMENT_PREMIUM_COST_LABEL = "Endorsement Premium (optional)";

// Helper function to parse deductible strings like "$5k/$25k" to separate numbers
function parseDeductibles (imNimString: string | null | undefined): { imDeductible: number | null; nimDeductible: number | null } {
	if (!imNimString) {
		return { imDeductible: null, nimDeductible: null };
	}

	// Remove dollar signs and split by forward slash
	const parts = imNimString.replace(/\$/g, "").split("/");
	
	if (parts.length !== 2) {
		return { imDeductible: null, nimDeductible: null };
	}

	const parseAmount = (amount: string): number | null => {
		const trimmed = amount.trim().toLowerCase();
		
		// Handle 'k' suffix for thousands
		if (trimmed.endsWith("k")) {
			const num = Number.parseFloat(trimmed.slice(0, -1));
			return Number.isNaN(num) ? null : num * 1000;
		}
		
		// Handle regular numbers
		const num = Number.parseFloat(trimmed);
		return Number.isNaN(num) ? null : num;
	};

	return {
		imDeductible: parseAmount(parts[0]),
		nimDeductible: parseAmount(parts[1]),
	};
}

export const UasDeductibleTypeSchema = z.enum(["fixed_amount", "percentage"]);
export type UasDeductibleType = z.infer<typeof UasDeductibleTypeSchema>;

const uasDeductibleText = (deductible: number, deductibleType: UasDeductibleType) =>
	deductibleType === "percentage" ? `${deductible}%` : `$${deductible}`;

export const uasDeductibleFields = (deductible: number | null, deductibleType: UasDeductibleType = "fixed_amount") => ({
	imNimDeductibles: deductible === null
		? null
		: `${uasDeductibleText(deductible, deductibleType)}/${uasDeductibleText(deductible, deductibleType)}`,
	imDeductible: deductible,
	nimDeductible: deductible,
	deductibleType,
});

export const formatUasDeductible = (
	amount: number | null | undefined,
	deductibleType: UasDeductibleType | null | undefined,
	whenNull = "NIL",
) => {
	if (amount === null || amount === undefined) {
		return whenNull;
	}
	return deductibleType === "percentage" ? `${amount}%` : formatCurrency(amount);
};

/** Normalize legacy `seating` JSON to `seats` (matches `aircraft.seats`). */
function quoteAircraftInputWithSeats (raw: unknown): unknown {
	if (!raw || typeof raw !== "object") {
		return raw;
	}
	const o = raw as Record<string, unknown>;
	const fromQuote = o.seats ?? o.seating;
	const seats = typeof fromQuote === "string"
		? fromQuote
		: fromQuote == null
			? ""
			: String(fromQuote);
	const rest = { ...o };
	delete rest.seating;
	return { ...rest, seats };
}

const quoteAircraftShape = {
	registration: z.string(),
	year: z.string(),
	make: z.string(),
	model: z.string(),
	seats: z.string(),
	useType: z.string(),
	subUse: z.string().nullable(),
	insuredValue: z.number(),
	hullRate: z.number(),
	hullPremium: z.number(),
	hullWarEnabled: z.boolean().default(true),
	hullWarRate: z.number().default(DEFAULT_HULL_WAR_RATE),
	hullWar: z.number(),
	imNimDeductibles: z.string().optional().nullable().default(null),
	imDeductible: z.number().optional().nullable().default(null),
	nimDeductible: z.number().optional().nullable().default(null),
	deductibleType: UasDeductibleTypeSchema.optional(),
	liabilityLimit: z.number(),
	perPassengerLiabilitySublimit: z.number().optional().nullable().default(null),
	liabilityPremium: z.number(),
	warLiabilityLimit: z.number().nullable(),
	warLiabilityEnabled: z.boolean().default(true),
	warLiabilityPremium: z.union([z.literal("Included"), z.literal("No war coverage"), z.number()]),
	totalPremium: z.number(),
	medicalPayments: z.number().optional().nullable().default(null),
	coverageLetter: z.union([z.literal("F"), z.literal("G"), z.literal("H")]).default("F"),
	agEnabled: z.boolean().optional(),
	chemicalEachPersonLimit: z.number().nullable().optional(),
	chemicalEachOccurrenceLimit: z.number().nullable().optional(),
	chemicalAggregateLimit: z.number().nullable().optional(),
	chemicalPremium: z.number().nullable().optional(),
};

function applyParsedDeductibles<T extends {
	imNimDeductibles?: string | null;
	imDeductible?: number | null;
	nimDeductible?: number | null;
}> (data: T): T {
	const { imDeductible, nimDeductible } = parseDeductibles(data.imNimDeductibles);

	return {
		...data,
		imDeductible: data.imDeductible ?? imDeductible,
		nimDeductible: data.nimDeductible ?? nimDeductible,
	};
}

export const QuoteAircraftSchema = z.preprocess(
	quoteAircraftInputWithSeats,
	z.object(quoteAircraftShape).transform(applyParsedDeductibles),
);

const numberOrZero = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : 0);

const isLegacyAlternateAircraft = (row: object) => !("hullWarEnabled" in row);

function alternateQuoteAircraftInput (raw: unknown): unknown {
	const normalized = quoteAircraftInputWithSeats(raw);
	if (!normalized || typeof normalized !== "object") {
		return normalized;
	}
	const { hullValue, ...rest } = normalized as Record<string, unknown>;
	const base = {
		...rest,
		registration: rest.registration ?? "",
		year: rest.year ?? "",
		make: rest.make ?? "",
		model: rest.model ?? "",
		useType: rest.useType ?? "",
		subUse: rest.subUse ?? null,
		insuredValue: numberOrZero(rest.insuredValue ?? hullValue),
		hullRate: numberOrZero(rest.hullRate),
		hullPremium: numberOrZero(rest.hullPremium),
		liabilityLimit: numberOrZero(rest.liabilityLimit),
		liabilityPremium: numberOrZero(rest.liabilityPremium),
		warLiabilityLimit: rest.warLiabilityLimit ?? null,
		totalPremium: numberOrZero(rest.totalPremium),
	};
	if (!isLegacyAlternateAircraft(rest)) {
		return base;
	}
	return {
		...base,
		hullWarEnabled: false,
		hullWarRate: 0,
		hullWar: 0,
		warLiabilityEnabled: false,
		warLiabilityPremium: 0,
	};
}

export const CglProductsSubRowVariantSchema = z.enum(["piston", "each_person"]);

export const CglFormOfBusinessSchema = z.enum([
	"individual",
	"partnership",
	"joint_venture",
	"trust",
	"limited_liability_company",
	"public_entity",
	"organization",
]);

export const CglQuoteLobDataSchema = z.object({
	lob: z.literal("commercial_general_liability"),
	generalAggregateLimit: z.number().nullable().default(null),
	eachOccurrenceLimit: z.number().nullable().default(null),
	eachPersonExProductsSublimit: z.number().nullable().default(null),
	eachOccurrencePremium: z.number().nullable().default(null),
	damageToPremisesRentedLimit: z.number().nullable().default(null),
	productsCompletedOpsTurbineAggregate: z.number().nullable().default(null),
	productsCompletedOpsTurbinePremium: z.number().nullable().default(null),
	productsCompletedOpsPistonAggregate: z.number().nullable().default(null),
	productsCompletedOpsSubVariant: CglProductsSubRowVariantSchema.default("piston"),
	personalAdvertisingInjuryAggregate: z.number().nullable().default(null),
	medicalExpenseEachPerson: z.number().nullable().default(null),
	hangarkeepersEachLossLimit: z.number().nullable().default(null),
	hangarkeepersEachAircraftLimit: z.number().nullable().default(null),
	hangarkeepersPremium: z.number().nullable().default(null),
	hangarkeepersDeductibleTurbine: z.number().nullable().default(null),
	hangarkeepersDeductibleOther: z.number().nullable().default(null),
	pdDeductibleTurbineAircraft: z.number().nullable().default(null),
	pdDeductibleOtherAircraft: z.number().nullable().default(null),
	pdDeductibleNonAircraft: z.number().nullable().default(null),
	premisesLocations: z.array(z.string()).default([]),
	formOfBusiness: CglFormOfBusinessSchema.nullable().default(null),
	endorsementPremium: z.number().nullable().default(null),
	stateTax: z.number().nullable().default(null),
	policyFee: z.number().nullable().default(null),
});

export const CglAlternatePremisesSchema = z.object({
	location: z.string().default(""),
	included: z.boolean().default(true),
});

export const CglAlternateSchema = z.object({
	lobData: CglQuoteLobDataSchema,
	premises: z.array(CglAlternatePremisesSchema).default([]),
});

export const UasScheduleTypeSchema = z.enum(["scheduled", "umbrella"]);

export const UasUmbrellaSchema = z.object({
	description: z.string().default(""),
	liabilityLimit: z.number().nullable().default(null),
	insuredValue: z.number().nullable().default(null),
	deductible: z.number().nullable().default(null),
	deductibleType: UasDeductibleTypeSchema.optional(),
	hullRate: z.number().default(0),
	hullPremium: z.number().default(0),
	liabilityPremium: z.number().default(0),
	hullWarEnabled: z.boolean().default(true),
	hullWarRate: z.number().default(0),
	warLiabilityEnabled: z.boolean().default(true),
	warLiabilityPremium: z.number().default(0),
	agEnabled: z.boolean().default(false),
	physicalDamageEnabled: z.boolean().default(false),
});

export const UasChemicalSchema = z.object({
	eachPersonLimit: z.number().nullable().default(null),
	eachOccurrenceLimit: z.number().nullable().default(null),
	aggregateLimit: z.number().nullable().default(null),
	premium: z.number().default(0),
});

export const UAS_PERSONAL_INJURY_CAP = 25_000_000;

export const UasTerritorySchema = z.enum(["north_america", "worldwide"]);

export const UasChemicalModeSchema = z.enum(["per_policy", "per_uas"]);

// Liability limits left null follow the highest scheduled (or umbrella) liability limit.
export const UasCoveragesSchema = z.object({
	nonOwnedLiability: z.number().nullable().default(null),
	newlyAcquiredLiability: z.number().nullable().default(null),
	premisesLiability: z.number().nullable().default(null),
	fireLegalLiability: z.number().nullable().default(25_000),
	saleOfAircraftProducts: z.number().nullable().default(null),
	contractualLiability: z.number().nullable().default(null),
	personalInjury: z.number().nullable().default(null),
	personalInjuryPurchased: z.boolean().default(true),
	unexpectedLandingPct: z.number().default(25),
	medPayEachPerson: z.number().nullable().default(null),
	medPayEachAccident: z.number().nullable().default(null),
	minimumEarnedPremiumPct: z.number().default(75),
	territory: UasTerritorySchema.default("north_america"),
});

export const UasQuoteLobDataSchema = z.object({
	lob: z.literal("uas"),
	scheduleType: UasScheduleTypeSchema.default("scheduled"),
	umbrella: UasUmbrellaSchema.nullable().default(null),
	chemical: UasChemicalSchema.prefault({}),
	chemicalMode: UasChemicalModeSchema.default("per_policy"),
	coverages: UasCoveragesSchema.prefault({}),
});

export const UasAlternateSchema = z.object({
	umbrella: UasUmbrellaSchema.nullable().default(null),
	chemical: UasChemicalSchema.prefault({}),
});

export const AlternateQuoteAircraftSchema = z.preprocess(
	alternateQuoteAircraftInput,
	z.object({
		...quoteAircraftShape,
		included: z.boolean().default(true),
	}).transform(applyParsedDeductibles),
);

export const AlternateQuoteSchema = z.object({
	id: z.string().default(() => crypto.randomUUID()),
	aircraft: z.array(AlternateQuoteAircraftSchema).default([]),
	cgl: CglAlternateSchema.nullable().default(null),
	uas: UasAlternateSchema.nullable().default(null),
});

export const CostLineItemSchema = z.object({
	commissionBearing: z.boolean(),
	label: z.string(),
	amount: z.number(),
});


export const EndorsementLimitSchema = z.object({
	qualifier: z.string().nullable(),
	amount: z.union([z.number(), z.literal("NIL")]).nullable(),
	mapTo: z.enum(["hull", "liability", "none"]).optional(),
	premiumIncluded: z.string(),
});

export const EndorsementSchema = z.object({
	name: z.string(),
	included: z.boolean(),
	limits: z.array(EndorsementLimitSchema),
});

export const PartySchema = z.object({
	name: z.string(),
	addressLine1: z.string(),
	addressLine2: z.string(),
	attention: z.string().optional().nullable(),
});

export const AdditionalInsuredSchema = PartySchema.extend({
	interestType: z.enum(["additional_insured", "lienholder", "crew_training"]).default("additional_insured").describe("The nature of the entity listed on the policy. For training providers, choose 'crew_training', for lienholders, choose 'lienholder', for any other types of additional insureds, choose 'additional_insured'."),
});

export const ProductsLiabilityQuoteLobDataSchema = z.object({
	lob: z.literal("product_liability"),
	coverageALimit: z.number().nullable().default(null),
	coverageAPremium: z.number().nullable().default(null),
	coverageBLimit: z.number().nullable().default(null),
	coverageBPremium: z.number().nullable().default(null),
	combinedLimit: z.number().nullable().default(null),
	splitLimits: z.array(z.object({
		limit: z.number(),
		premium: z.number().nullable().default(null),
		description: z.string(),
	})).default([]),
	triaLiabilityLimit: z.number().nullable().default(null),
	triaLiabilityPremium: z.number().nullable().default(null),
	endorsementPremium: z.number().nullable().default(null),
	stateTax: z.number().nullable().default(null),
	policyFee: z.number().nullable().default(null),
});

export const QuoteLobDataSchema = z.discriminatedUnion("lob", [
	ProductsLiabilityQuoteLobDataSchema,
	CglQuoteLobDataSchema,
	UasQuoteLobDataSchema,
]);

export const defaultProductsLiabilityLobData = (extracted: {
	coverageALimit?: number | null;
	coverageBLimit?: number | null;
	aggregateLimit?: number | null;
} | null): ProductsLiabilityQuoteLobData => ({
	lob: "product_liability",
	coverageALimit: extracted?.coverageALimit ?? null,
	coverageAPremium: null,
	coverageBLimit: extracted?.coverageBLimit ?? null,
	coverageBPremium: null,
	combinedLimit: extracted?.aggregateLimit ?? null,
	triaLiabilityLimit: extracted?.aggregateLimit ?? null,
	triaLiabilityPremium: null,
	endorsementPremium: null,
	stateTax: null,
	policyFee: 500,
	splitLimits: [],
});

export function plCorePremiumFromLobData (lobData: null | ProductsLiabilityQuoteLobData | undefined): number {
	const splitLimitsPremium = (lobData?.splitLimits ?? []).reduce((sum, split) => sum + (split.premium ?? 0), 0);
	return (lobData?.coverageAPremium ?? 0)
		+ (lobData?.coverageBPremium ?? 0)
		+ splitLimitsPremium
		+ (lobData?.triaLiabilityPremium ?? 0);
}

export const defaultCglLobData = (extracted: {
	premisesLocations?: string[] | null;
	generalAggregateLimit?: number | null;
	eachOccurrenceLimit?: number | null;
	productsCompletedOpsAggregateLimit?: number | null;
	hangarkeepersEachLossLimit?: number | null;
	hangarkeepersEachAircraftLimit?: number | null;
} | null): CglQuoteLobData => ({
	lob: "commercial_general_liability",
	generalAggregateLimit: extracted?.generalAggregateLimit ?? null,
	eachOccurrenceLimit: extracted?.eachOccurrenceLimit ?? null,
	eachPersonExProductsSublimit: null,
	eachOccurrencePremium: null,
	damageToPremisesRentedLimit: null,
	// The turbine row is the primary one; the piston row below it is an underwriter carve-out.
	productsCompletedOpsTurbineAggregate: extracted?.productsCompletedOpsAggregateLimit ?? null,
	productsCompletedOpsTurbinePremium: null,
	productsCompletedOpsPistonAggregate: null,
	productsCompletedOpsSubVariant: "piston",
	personalAdvertisingInjuryAggregate: null,
	medicalExpenseEachPerson: null,
	hangarkeepersEachLossLimit: extracted?.hangarkeepersEachLossLimit ?? null,
	hangarkeepersEachAircraftLimit: extracted?.hangarkeepersEachAircraftLimit ?? null,
	hangarkeepersPremium: null,
	hangarkeepersDeductibleTurbine: null,
	hangarkeepersDeductibleOther: null,
	pdDeductibleTurbineAircraft: null,
	pdDeductibleOtherAircraft: null,
	pdDeductibleNonAircraft: null,
	premisesLocations: extracted?.premisesLocations ?? [],
	formOfBusiness: null,
	endorsementPremium: null,
	stateTax: null,
	policyFee: 500,
});

export const defaultUasUmbrella = ({ agEnabled }: { agEnabled: boolean }): UasUmbrella =>
	UasUmbrellaSchema.parse({ agEnabled });

export const uasUmbrellaCoverage = (umbrella: UasUmbrella): UasUmbrella => (umbrella.physicalDamageEnabled
	? umbrella
	: {
		...umbrella,
		insuredValue: null,
		deductible: null,
		hullRate: 0,
		hullPremium: 0,
		hullWarEnabled: false,
		hullWarRate: 0,
	});

export const defaultUasLobData = (): UasQuoteLobData => ({
	lob: "uas",
	scheduleType: "scheduled",
	umbrella: null,
	chemical: UasChemicalSchema.parse({}),
	chemicalMode: "per_policy",
	coverages: UasCoveragesSchema.parse({}),
});

export const QuoteUpdateSchema = z.object({
	insured: PartySchema,
	producer: PartySchema,
	aircraft: z.array(QuoteAircraftSchema),
	costs: z.array(CostLineItemSchema),
	endorsementsLegacy: z.array(EndorsementSchema),
	inceptionDate: z.iso.date(),
	expiryDate: z.iso.date(),
	brokerCommission: z.number(),
	omitBrokerCommission: z.boolean().default(false),
	pilotWarrantyMarkdown: z.string().optional(),
	underwriterComments: z.string().optional(),
	alternateQuotes: z.array(AlternateQuoteSchema).optional(),
	lineSize: z.number(),
	lineLead: z.string(),
	lineFollow: z.string().optional().nullable(),
	lineRole: z.enum(["lead", "follow"]).default("lead"),
	leadPolicyNumber: z.string().optional().nullable(),
	lobData: QuoteLobDataSchema.nullable().optional(),
});

export const AircraftQuotePolicyContextSchema = z.object({
	policyNumber: z.string(),
	insured: PartySchema,
	producer: PartySchema,
	policyUseType: z.string(),
	inceptionDate: z.string(),
	expiryDate: z.string(),
	brokerCommission: z.number(),
	underwriter: z.string(),
	lineLead: z.string(),
	lineFollow: z.string().nullable(),
	lineSize: z.number(),
});

export const AircraftAdditionAlternateSchema = z.object({
	aircraft: QuoteAircraftSchema,
	premium: z.number(),
	totalDue: z.number(),
});

export const AircraftAdditionQuotePayloadSchema = AircraftQuotePolicyContextSchema.extend({
	effectiveDate: z.string(),
	aircraft: QuoteAircraftSchema,
	premium: z.number(),
	totalDue: z.number(),
	alternates: z.array(AircraftAdditionAlternateSchema).default([]),
});

export type AircraftQuotePolicyContext = z.output<typeof AircraftQuotePolicyContextSchema>;
export type AircraftAdditionAlternate = z.output<typeof AircraftAdditionAlternateSchema>;
export type AircraftAdditionQuotePayload = z.output<typeof AircraftAdditionQuotePayloadSchema>;
export type AlternateQuote = z.output<typeof AlternateQuoteSchema>;
export type AlternateQuoteAircraft = z.output<typeof AlternateQuoteAircraftSchema>;
export type CostLineItem = z.output<typeof CostLineItemSchema>;
export type Endorsement = z.output<typeof EndorsementSchema>;
export type EndorsementLimit = z.output<typeof EndorsementLimitSchema>;
export type Party = z.output<typeof PartySchema>;
export type AdditionalInsured = z.output<typeof AdditionalInsuredSchema>;
export type ProductsLiabilityQuoteLobData = z.output<typeof ProductsLiabilityQuoteLobDataSchema>;

export type CglQuoteLobData = z.output<typeof CglQuoteLobDataSchema>;

export type CglProductsSubRowVariant = z.output<typeof CglProductsSubRowVariantSchema>;

export type CglFormOfBusiness = z.output<typeof CglFormOfBusinessSchema>;

/** Wording follows the FORM OF BUSINESS boxes on the CGL declarations. */
export const cglFormOfBusinessLabels: Record<CglFormOfBusiness, string> = {
	individual: "Individual",
	partnership: "Partnership",
	joint_venture: "Joint Venture",
	trust: "Trust",
	limited_liability_company: "Limited Liability Company",
	public_entity: "A Public Entity",
	organization: "Organization, including a corporation",
};

export const cglProductsSubRowVariants: Record<CglProductsSubRowVariant, {
	label: string;
	qualifier: string;
}> = {
	piston: {
		label: "Solely as respects Piston Engine Aircraft",
		qualifier: "aggregate",
	},
	each_person: {
		label: "Subject to a maximum limit each person",
		qualifier: "each person",
	},
};

export const cglProductsSubRow = (lobData: CglQuoteLobData | null | undefined) => ({
	...cglProductsSubRowVariants[lobData?.productsCompletedOpsSubVariant ?? "piston"],
	shown: lobData?.productsCompletedOpsPistonAggregate != null,
});

export type CglAlternate = z.output<typeof CglAlternateSchema>;

export type CglAlternatePremises = z.output<typeof CglAlternatePremisesSchema>;
export type QuoteAircraft = z.output<typeof QuoteAircraftSchema>;
export type UasScheduleType = z.output<typeof UasScheduleTypeSchema>;
export type UasUmbrella = z.output<typeof UasUmbrellaSchema>;
export type UasChemical = z.output<typeof UasChemicalSchema>;
export type UasCoverages = z.output<typeof UasCoveragesSchema>;
export type UasTerritory = z.output<typeof UasTerritorySchema>;
export type UasChemicalMode = z.output<typeof UasChemicalModeSchema>;
export type UasQuoteLobData = z.output<typeof UasQuoteLobDataSchema>;
export type UasAlternate = z.output<typeof UasAlternateSchema>;
export type QuoteLobData = z.output<typeof QuoteLobDataSchema>;
export type QuoteUpdate = z.output<typeof QuoteUpdateSchema>;

export const withDerivedHullPremiums = (ac: QuoteAircraft): QuoteAircraft => {
	const hullPremium = roundCurrency2(ac.hullRate * (ac.insuredValue / 100));
	const hullWar = ac.hullWarEnabled ? roundCurrency2(ac.hullWarRate * (ac.insuredValue / 100)) : 0;
	const warLiabilityPremium = ac.warLiabilityEnabled
		&& typeof ac.warLiabilityPremium === "number"
		&& !Number.isNaN(ac.warLiabilityPremium)
		? ac.warLiabilityPremium
		: 0;
	return {
		...ac,
		hullPremium,
		hullWar,
		totalPremium: hullPremium + ac.liabilityPremium + hullWar + warLiabilityPremium,
	};
};
